/**
 * Back-office — Factures partenaires (permission partners.manage).
 * BCL télécharge la facture déposée par le partenaire et la marque payée.
 */
import type { BackofficeInvoice } from '@/features/partners/partner-invoices-api';
import type { PartnerInvoiceStatus } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { extractErrorMessage } from '@/features/auth/error-message';
import {
  getInvoiceDownloadUrl,
  listAllInvoices,
  payInvoice,
} from '@/features/partners/partner-invoices-api';

const statusBadge: Record<PartnerInvoiceStatus, { label: string; cls: string }> = {
  a_payer: { label: 'À payer', cls: 'bg-amber-100 text-amber-800' },
  paye: { label: 'Payée', cls: 'bg-green-100 text-green-800' },
};

const filters: { value: PartnerInvoiceStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Toutes' },
  { value: 'a_payer', label: 'À payer' },
  { value: 'paye', label: 'Payées' },
];

export function AdminPartnerInvoicesPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<PartnerInvoiceStatus | 'all'>('all');
  const [refs, setRefs] = useState<Record<string, string>>({});

  const invoices = useQuery({
    queryKey: ['admin', 'partner-invoices', filter],
    queryFn: () => listAllInvoices(filter === 'all' ? undefined : filter),
  });

  const payMut = useMutation({
    mutationFn: ({ id, reference }: { id: string; reference?: string }) =>
      payInvoice(id, reference),
    onSuccess: () => {
      toast.success('Facture marquée payée');
      qc.invalidateQueries({ queryKey: ['admin', 'partner-invoices'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  async function download(id: string) {
    try {
      const url = await getInvoiceDownloadUrl(id);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  }

  function renderAction(invoice: BackofficeInvoice) {
    if (invoice.status === 'paye') {
      return (
        <span className="text-xs text-gray-500">
          {invoice.paymentReference ?? '—'}
        </span>
      );
    }
    return (
      <div className="flex items-center justify-end gap-2">
        <Input
          placeholder="Réf. virement"
          className="h-8 w-32"
          value={refs[invoice.id] ?? ''}
          onChange={(e) => setRefs({ ...refs, [invoice.id]: e.target.value })}
        />
        <Button
          size="sm"
          className="bg-green-600 hover:bg-green-700"
          disabled={payMut.isPending}
          onClick={() =>
            payMut.mutate({ id: invoice.id, reference: refs[invoice.id] || undefined })
          }
        >
          Marquer payée
        </Button>
      </div>
    );
  }

  return (
    <DashboardLayout variant="admin" title="Factures partenaires">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Factures sous-traitants</CardTitle>
            <CardDescription>
              {invoices.data ? `${invoices.data.length} facture(s)` : 'Chargement…'}
            </CardDescription>
          </div>
          <div className="flex gap-1">
            {filters.map((f) => (
              <Button
                key={f.value}
                size="sm"
                variant={filter === f.value ? 'default' : 'outline'}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Partenaire</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Facture</TableHead>
                <TableHead className="text-right">Paiement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.data?.length ? (
                invoices.data.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">
                      {inv.partnerName ?? '—'}
                    </TableCell>
                    <TableCell>{(inv.amountCents / 100).toFixed(2)} €</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusBadge[inv.status].cls}>
                        {statusBadge[inv.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => download(inv.id)}
                        aria-label="Télécharger la facture"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">{renderAction(inv)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-gray-500">
                    Aucune facture.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
